import { LandingPageContent } from '../types';

const birthdayGiftsForKids: LandingPageContent = {
	path: '/gifts/birthday-gifts-for-kids',
	seoTitle: "Birthday Gifts for Kids — Personalized Presents That Last",
	metaDescription:
		"Birthday gift ideas for children aged 1–10, from personalized storybooks to custom prints. What to buy, what to avoid, and how far ahead to order.",
	heading: 'Birthday Gifts for Kids',
	subheading: 'What to buy when you want it to still matter in a year.',
	heroImage: '/assets/hero/kid-4/watercolor_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Children's birthdays have an unusual problem: the child gets a lot of things at once, in a short window, in front of an audience. Most of it blurs.

The gifts that don't blur tend to have one of two qualities — either they're the thing the child specifically asked for, or they're unmistakably *about* that child. If you're not in a position to know the first, the second is a reliable fallback.`,
	sections: [
		{
			heading: 'By age',
			body: `**Turning 1.** The gift is really for the parents; the child will be more interested in the box. Choose something that will be kept — a personalized book they'll grow into, a printed keepsake. First-birthday gifts get photographed and stored more than any other year.

**Turning 2 or 3.** Recognition is the whole game. A child this age seeing their own face in a book will flip back to the same page repeatedly. Keep the story simple.

**Turning 4 or 5.** Peak personalized-storybook age. Old enough for a plot, young enough to fully accept that they personally rescued the dragon.

**Turning 6 or 7.** Early reading. A book where the hero has their name is a genuinely useful nudge — it converts reading practice into something with stakes.

**Turning 8 to 10.** Move away from picture books. A custom t-shirt with their illustrated character, or a poster for their room, ages better.`,
		},
		{
			heading: 'Gifts to be wary of',
			body: `A few patterns that consistently disappoint:

- **Anything requiring an adult to set it up every time.** It will be used twice.
- **Very large toys**, unless you have checked with the parents. Floor space is a finite and contested resource.
- **Name-printed mass-produced items.** A name on a generic product reads as monogramming, not personalization, and children work this out quickly.
- **Digital-only gifts for young children.** A download is not something you can hand over at a party.`,
		},
		{
			heading: 'When to order',
			body: `Anything made to order needs lead time. For a personalized book, the sequence is: order, artwork generated, you review and approve, print, ship.

Two to three weeks before the birthday is comfortable. One week is possible but leaves no slack if you want a page redone. In November and December, add more — print production slows across the whole industry in the run-up to Christmas.`,
		},
		{
			heading: 'If you are buying from far away',
			body: `Grandparents, godparents and relatives abroad have the hardest version of this problem: no reliable read on what the child is into this month, and shipping to consider.

Personalized gifts solve most of it. They're distinctive without requiring inside knowledge of current obsessions, they ship internationally, and they carry an obvious signal of effort — which, when you can't be there in person, is much of the point.

You'll need one photo from the parents. That's usually a manageable ask.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'Best for ages 2–8. The default choice for a birthday.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Best for ages 3–7, and for parties where kids need an activity.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'Ages 5+. Wearable, and survives being outgrown as a keepsake.',
		},
	],
	faqs: [
		{
			question: 'What is the best birthday gift for a 5-year-old?',
			answer:
				'A personalized storybook is a strong default at five. Children this age can follow a narrative but still fully accept themselves as the hero of it, which makes the personalization land rather than feel novelty.',
		},
		{
			question: 'How far in advance should I order a personalized birthday gift?',
			answer:
				'Two to three weeks is comfortable. The book is only printed after you approve the artwork, so leave room for a page to be regenerated if needed. Order earlier for December birthdays.',
		},
		{
			question: 'What birthday gifts do children actually keep?',
			answer:
				'Gifts where the child is the subject rather than the recipient tend to be kept longest — personalized books, printed portraits, and keepsakes that get shelved rather than played with and outgrown.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Gifts for 3-year-olds', href: '/gifts/gifts-for-3-year-olds' },
		{
			label: 'Personalized gifts for a 4-year-old',
			href: '/blog/personalized-gifts-for-4-year-olds',
		},
	],
	cta: { label: 'Find a birthday gift', href: '/products' },
};

export default birthdayGiftsForKids;
